import {
  FABRIC_PRODUCT_OPTION_ID,
  LEATHER_PRODUCT_OPTION_ID,
  MIXED_FABRIC_PRODUCT_OPTION_ID,
  product_item_availability,
} from 'commons/constants';
import convertOptionParamFormat from 'commons/convertOptionParamFormat';
import {
  getPriceDetailsUtil,
  findAvailabilityAndVisibility,
} from 'data-hooks/utils';
import { getOptionAdditionalPrice } from 'data-hooks/useProductPrice/utils';

/**
 * Builds the PDP's ProductGroup JSON-LD. Pure so that the server render and the
 * client produce identical markup, and so the pricing rules can be tested directly.
 *
 * Uses every price input the server has: base price, promotion, option upcharges
 * and exclude_from_sale. Excluded are the inputs that only exist client-side —
 * clearance and stockOptions upcharges (per-ZIP, per-warehouse), trade discounts
 * (auth) and the Decide Later tab (UI state).
 *
 * Availability follows the same rule: the product-level discontinued flag and
 * item_availability attribute, never the per-ZIP stock query.
 */

// The PDP's isOutOfStock is derived from per-ZIP stock, so it is absent during a
// server render and can flip after hydration. These two flags are national and
// carried on basicDetails.
export const isNationallyOutOfStock = basicDetails => {
  if (basicDetails?.discontinued) {
    return true;
  }

  const { availability } = findAvailabilityAndVisibility(
    basicDetails?.attributes || []
  );

  return (
    `${availability}` === `${product_item_availability.out_of_stock}` ||
    `${availability}` === `${product_item_availability.disabled}`
  );
};

// Matches pdpOptionUtils' isUpholsteryOption. Vendor Leather (165) is
// deliberately excluded: it is absent from VALID_IMAGE_OPTION_IDS, so config
// images for those variants are not built (CON-8772 review).
const SEO_VARIANT_OPTION_IDS = [
  FABRIC_PRODUCT_OPTION_ID,
  LEATHER_PRODUCT_OPTION_ID,
  MIXED_FABRIC_PRODUCT_OPTION_ID,
];

export const isSEOVariantOption = id =>
  SEO_VARIANT_OPTION_IDS.includes(parseInt(id, 10));

// The selection the PDP starts from, so the published price matches what a
// visitor landing on the canonical URL is shown.
const getDefaultSelectionIds = (options = []) =>
  options.reduce((selection, option) => {
    const defaultValue =
      option.values?.find(
        value => `${value.id}` === `${option.default_value}`
      ) ?? option.values?.[0];

    if (defaultValue?.id != null) {
      // eslint-disable-next-line no-param-reassign
      selection[option.id] = defaultValue.id;
    }

    return selection;
  }, {});

const resolvePrice = (priceObj, promotion, additionalCost, excludeFromSale) => {
  if (!priceObj || !Number.isFinite(priceObj.original_price)) {
    return undefined;
  }

  // Mirrors usePDPPrice: an excluded ("Limited Edition") value drops the
  // percentage discount for an anonymous visitor but keeps promotional tiers.
  const effectivePrice = excludeFromSale
    ? { ...priceObj, discount_percent: 0, max_discount_percent: 0 }
    : priceObj;

  // isClearance not passed: it is per-ZIP, per-warehouse and client-only.
  const details = getPriceDetailsUtil(
    effectivePrice,
    promotion,
    additionalCost
  );
  const price = details.hasTieredDiscount
    ? details.leastTieredDiscountedPrice
    : details.salePriceWithOptionsPrice;

  // An empty price object yields NaN, which survives `!= null` and serialises
  // as a literal `null` — a harder validation failure than an absent field.
  return Number.isFinite(price) ? price : undefined;
};

const buildOffer = ({ price, url, priceValidUntil, isOutOfStock }) => {
  const offer = {
    '@type': 'Offer',
    priceCurrency: 'USD',
    availability: isOutOfStock
      ? 'https://schema.org/OutOfStock'
      : 'https://schema.org/InStock',
    itemCondition: 'https://schema.org/NewCondition',
  };

  if (url) offer.url = url;
  if (price != null) offer.price = price;
  if (priceValidUntil) offer.priceValidUntil = priceValidUntil;

  return offer;
};

const buildVariants = ({
  upholsteryOption,
  options,
  canonicalURL,
  name,
  sku,
  priceObj,
  promotion,
  priceValidUntil,
  isOutOfStock,
  heroImageByUpholsteryName,
  fallbackImage,
}) => {
  const { finalOptionName: optionParamName } = convertOptionParamFormat(
    upholsteryOption.name,
    ''
  );
  const defaultSelection = getDefaultSelectionIds(options);

  return upholsteryOption.values
    .filter(value => value.is_active !== 0)
    .map(optionValue => {
      const { finalOptionValue: optionValueSlug } = convertOptionParamFormat(
        '',
        optionValue.value
      );

      const variantUrl = canonicalURL
        ? `${canonicalURL}?${encodeURIComponent(
            optionParamName
          )}=${encodeURIComponent(optionValueSlug)}`
        : undefined;

      // Same helper the PDP price uses, with only the upholstery swapped.
      // Subtracting from a precomputed optionPrice broke on the Decide Later tab.
      const additionalCost = getOptionAdditionalPrice(
        options,
        null,
        { ...defaultSelection, [upholsteryOption.id]: optionValue.id },
        true,
        upholsteryOption.id
      );

      // usePDPPrice only honours exclude_from_sale for Fabric (17), so match it
      // exactly — diverging would mismatch the price Google sees on the page.
      const excludeFromSale =
        upholsteryOption.id === FABRIC_PRODUCT_OPTION_ID &&
        Boolean(optionValue.exclude_from_sale);

      const variant = {
        '@type': 'Product',
        sku: sku != null ? `${sku}${optionValue.sku || ''}` : undefined,
        image:
          heroImageByUpholsteryName[optionValue.value?.toLowerCase()?.trim()] ||
          optionValue.image ||
          fallbackImage,
        color: optionValue.value,
        offers: buildOffer({
          price: resolvePrice(
            priceObj,
            promotion,
            additionalCost,
            excludeFromSale
          ),
          url: variantUrl,
          priceValidUntil,
          isOutOfStock,
        }),
      };

      if (name) {
        variant.name = `${name} in ${optionValue.value}`;
      }
      if (variantUrl) {
        variant['@id'] = variantUrl;
        variant.url = variantUrl;
      }

      return variant;
    });
};

const buildProductGroupJsonLd = ({
  name,
  description,
  sku,
  canonicalURL,
  options = [],
  priceObj,
  promotion,
  priceValidUntil,
  isOutOfStock = false,
  heroImageByUpholsteryName = {},
  fallbackImage = '',
}) => {
  const productGroup = {
    '@context': 'https://schema.org/',
    '@type': 'ProductGroup',
    name,
    description,
    brand: { '@type': 'Brand', name: 'Joybird' },
  };

  if (sku != null) {
    productGroup.productGroupID = sku;
  }
  if (canonicalURL) {
    productGroup['@id'] = canonicalURL;
    productGroup.url = canonicalURL;
  }

  const upholsteryOption = options.find(option =>
    isSEOVariantOption(option.id)
  );

  if (upholsteryOption?.values?.length) {
    const variants = buildVariants({
      upholsteryOption,
      options,
      canonicalURL,
      name,
      sku,
      priceObj,
      promotion,
      priceValidUntil,
      isOutOfStock,
      heroImageByUpholsteryName,
      fallbackImage,
    });

    // Publishing variants with no price is what put 467 of 488 indexable pages
    // into Search Console as rich-result errors (CON-8772). Emit nothing
    // instead: absent markup is not an error, invalid markup is.
    const pricedVariants = variants.filter(
      variant => variant.offers.price != null
    );

    if (pricedVariants.length === 0) {
      return null;
    }

    productGroup.variesBy = ['https://schema.org/color'];
    productGroup.hasVariant = pricedVariants;

    return productGroup;
  }

  // Products with no upholstery option (tables, rugs) keep price/stock/image at
  // the group level, matching the single-Product schema that preceded this.
  const price = resolvePrice(
    priceObj,
    promotion,
    getOptionAdditionalPrice(
      options,
      null,
      getDefaultSelectionIds(options),
      true
    )
  );

  if (price == null) {
    return null;
  }

  productGroup.image = fallbackImage || undefined;
  productGroup.sku = sku;
  productGroup.offers = buildOffer({
    price,
    url: canonicalURL,
    priceValidUntil,
    isOutOfStock,
  });

  return productGroup;
};

// `<` must be escaped or a description containing "</script>" closes the tag early.
export const toJsonLdScriptContent = jsonLd =>
  jsonLd ? JSON.stringify(jsonLd).replace(/</g, '\\u003c') : null;

export default buildProductGroupJsonLd;
